import { memo, useCallback, useEffect, useState, type ReactNode, type JSX } from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Button } from '@mui/material';
import {
  MaterialReactTable,
  MRT_BottomToolbar,
  useMaterialReactTable,
  type MRT_TableInstance,
  type MRT_ColumnFiltersState,
  MRT_SortingState,
  MRT_PaginationState,
  MRT_ColumnDef,
} from 'material-react-table';
import { MRT_Localization_RU } from 'material-react-table/locales/ru';
import moment from 'moment';

import { getFilterMatches } from './filters';
import { exportToCSV, getStorageKey, loadColumnSettings } from './helpers';
import { useDebouncedState } from '../../hooks/useDebouncedState';
import { Criteria, PageParams, SortType } from '../../types/pagination';
import { isValidDate } from '../../utils';

type Props<T extends Record<string, unknown>> = {
  name: string;
  columns: MRT_ColumnDef<T>[];
  data: T[];
  columnSorts?: MRT_SortingState;
  columnFilters?: MRT_ColumnFiltersState;
  columnVisibility?: Record<string, boolean>;
  pageSize?: number;
  totalRowCount: number;
  serverMode?: boolean;
  isLoading?: boolean;
  onFetchData?: (params: PageParams) => void;
  showColumnDividers?: boolean;
  enablePagination?: boolean;
  bottomToolbarCustomActions?: ReactNode;
};

// eslint-disable-next-line react/display-name
const TableGrid = memo(
  <T extends Record<string, unknown>>({
    name,
    columns,
    data,
    onFetchData,
    serverMode = false,
    isLoading = false,
    totalRowCount = 0,
    columnSorts = [],
    columnFilters = [],
    columnVisibility = {},
    pageSize = 10,
    showColumnDividers = false,
    enablePagination = true,
    bottomToolbarCustomActions,
  }: Props<T>) => {
    const [sorting, setSorting] = useState<MRT_SortingState>(columnSorts);
    const [rawFilters, setRawFilters] = useState<MRT_ColumnFiltersState>(columnFilters);

    const [filters, setFilters] = useDebouncedState<MRT_ColumnFiltersState>(columnFilters, 500);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
      pageIndex: 0,
      pageSize,
    });
    const [columnVisibilityState, setColumnVisibilityState] = useState<Record<string, boolean>>(
      loadColumnSettings(name, columnVisibility),
    );

    const handleDownload = useCallback(async (table: MRT_TableInstance<T>) => {
      const lastHeaderGroup = table.getHeaderGroups().at(-1);
      if (!lastHeaderGroup) {
        console.error('Заголовки таблицы не найдены: ', table.getHeaderGroups());
        return [];
      }

      const headers = lastHeaderGroup.headers
        .filter((h) => h.column.getIsVisible())
        .filter((h) => Boolean(h.column.columnDef.header))
        .map((header) => {
          return header.column.columnDef.header as string;
        });

      const exportRows = table.getSortedRowModel().rows;

      const data = exportRows.map((row) => {
        const cells = row.getVisibleCells().filter((cell) => Boolean(cell.column.columnDef.header));
        const values = cells.map((cell) => {
          const value = cell.getValue();
          const isNumericString = typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value));

          if (
            value !== null &&
            value !== undefined &&
            value !== '' &&
            !isNumericString &&
            typeof value !== 'number' &&
            isValidDate(value as string)
          ) {
            return moment(value as string).format('DD.MM.YYYY HH:mm');
          }

          return cell.getValue() ?? '';
        });

        return values.join(', ');
      });

      const th = headers.join(', ');
      const td = data.join('\n');

      const text = `${th}\n${td}`;
      exportToCSV(text);
    }, []);

    const handleFetchData = useCallback(() => {
      if (serverMode && onFetchData) {
        const sort = sorting.length > 0 ? sorting[0] : { id: '', desc: false };

        onFetchData({
          pageNumber: pagination.pageIndex,
          pageSize: pagination.pageSize,
          sortFieldName: sort.id,
          sortDirection: sort.desc ? SortType.Desk : SortType.Ask,
          filters: filters.map((filter) => ({
            fieldName: filter.id,
            filterCriteria: Array.isArray(filter.value) && filter.value.length ? Criteria.Equals : Criteria.Contains,
            matches: getFilterMatches(filter.value),
          })),
        });
      }
    }, [serverMode, onFetchData, sorting, pagination.pageIndex, pagination.pageSize, filters]);

    useEffect(() => {
      setFilters(rawFilters);
    }, [rawFilters, setFilters]);

    useEffect(() => {
      handleFetchData();
    }, [handleFetchData]);

    useEffect(() => {
      localStorage.setItem(getStorageKey(name), JSON.stringify(columnVisibilityState));
    }, [columnVisibilityState, name]);

    const getColumnDividerSx = useCallback(
      (columnId: string, visibleColumnIds: string[]) => {
        if (!showColumnDividers) {
          return undefined;
        }

        const isLastVisibleColumn = visibleColumnIds[visibleColumnIds.length - 1] === columnId;

        if (isLastVisibleColumn) {
          return undefined;
        }

        return {
          borderRight: (theme: { palette: { divider: string } }) => `1px solid ${theme.palette.divider}`,
        };
      },
      [showColumnDividers],
    );

    const table = useMaterialReactTable<T>({
      columns,
      data,
      localization: MRT_Localization_RU,
      columnFilterDisplayMode: 'popover',
      manualSorting: serverMode,
      enablePagination,
      manualPagination: serverMode,
      manualFiltering: serverMode,
      pageCount: Math.ceil(totalRowCount / pagination.pageSize),
      rowCount: totalRowCount,
      onPaginationChange: setPagination,
      onSortingChange: setSorting,
      onColumnFiltersChange: setRawFilters,
      onColumnVisibilityChange: setColumnVisibilityState,
      muiTableContainerProps: {
        sx: { maxWidth: '100%', overflowX: 'auto' },
      },
      muiTableHeadCellProps: ({ column, table }) => ({
        sx: getColumnDividerSx(
          column.id,
          table.getVisibleLeafColumns().map((visibleColumn) => visibleColumn.id),
        ),
      }),
      muiTableBodyCellProps: ({ cell, table }) => ({
        sx: getColumnDividerSx(
          cell.column.id,
          table.getVisibleLeafColumns().map((visibleColumn) => visibleColumn.id),
        ),
      }),
      initialState: {
        showColumnFilters: true,
        density: 'compact',
        columnVisibility: columnVisibilityState,
        sorting,
        isLoading,
      },
      state: {
        columnFilters: rawFilters,
        columnVisibility: columnVisibilityState,
        pagination,
        sorting,
        isLoading,
      },
      renderTopToolbarCustomActions: ({ table }) => (
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            padding: '8px',
            flexWrap: 'wrap',
          }}
        >
          <Button
            disabled={table.getFilteredRowModel().rows.length === 0}
            onClick={() => handleDownload(table)}
            startIcon={<FileDownloadIcon />}
          >
            Скачать CSV
          </Button>
        </Box>
      ),
      renderBottomToolbar: bottomToolbarCustomActions
        ? ({ table }) => (
            <>
              <Box
                sx={{
                  alignItems: 'center',
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 52,
                  px: 1,
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                {bottomToolbarCustomActions}
              </Box>
              <MRT_BottomToolbar table={table} />
            </>
          )
        : undefined,
    });

    return <MaterialReactTable table={table} />;
  },
);

export const TableGridLocal = TableGrid as <T extends Record<string, unknown>>(props: Props<T>) => JSX.Element;
